/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';

import { getPageOnHostByID } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import trackAction from 'utils/tracking';

import Embed from 'components/Core/Embed';
import HeroSlideshow from 'components/Core/HeroSlideshow';
import RichText from 'components/Core/RichText';
import Facility from 'components/Facility';
import CtaBlock from 'components/CtaBlock';
import OtherServices from 'components/OtherServices';
import QuickNavigation from 'components/QuickNavigation';
import ActivityTheme from 'components/ActivityTheme';
import LatestNews from 'components/LatestNews';

const PartnerHomepage = (props) => {
  const {
    page: { carousel, body, heroTitle, heroSubtitle },
    siteName,
  } = props;

  const onQNavItemClick = (itemLabel) => {
    trackAction('quick-navigation', itemLabel, 'click');
  };

  const onServiceClickCB = (serviceCategory, serviceName) => {
    trackAction('partner-homepage', serviceName, serviceCategory);
  };

  const activityThemeData = {
    siteName,
    title: heroTitle,
    subtitle: heroSubtitle,
  };

  return (
    <>
      {carousel.length > 0 && <HeroSlideshow extraClassNames="mb-6 lg:mb-12" carousel={carousel} />}
      {activityThemeData && (
        <div className="relative -mt-10 lg:-mt-20">
          <ActivityTheme {...activityThemeData} showWizardSetting={false} />
        </div>
      )}
      <div className="u-block-margins" />
      {(body || []).map((block) => {
        if (block.blockType === 'QuickNavigationBlock') {
          return <QuickNavigation {...block} onItemClickCB={onQNavItemClick} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'ServicesBlock') {
          return <Facility {...block} key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'OtherServicesBlock') {
          return <OtherServices {...block} key={`${block.blockType}-${block.id}`} onClickService={onServiceClickCB} />;
        }
        if (block.blockType === 'CTABlock') {
          return <CtaBlock {...block} fullWidth key={`${block.blockType}-${block.id}`} />;
        }
        if (block.blockType === 'RichTextBlock') {
          return (
            <div key={`${block.blockType}-${block.id}`} className="container mx-auto px-4">
              <div className="flex flex-wrap">
                <div className="flex-full max-w-full lg:flex-2/3 lg:max-w-2/3">
                  <RichText content={block.value} />
                </div>
              </div>
            </div>
          );
        }
        if (block.blockType === 'CustomEmbedBlock') {
          return (
            <div key={`${block.blockType}-${block.id}`} className="container mx-auto px-4">
              <div className="u-block-margins">
                <Embed title={block.title} {...block.embed} />
              </div>
            </div>
          );
        }
        if (block.blockType === 'LatestNewsBlock') {
          return <LatestNews {...block} key={`${block.blockType}-${block.id}`} />;
        }
        return <div key={`${block.blockType}-${block.id}`} />;
      })}
    </>
  );
};

// The data returned here will be send as `props` to the component
// TODO: Let's see if we need this here or can ultimately adopt this into the lib/api calls directly
// That will depend on how different these calls are going to be along the way.
PartnerHomepage.getCustomInitialProps = async ({ id, contentType, hostname, previewData }) => {
  const query = requireQueryByName('PartnerHomepage');
  const token = previewData ? previewData.token : '';
  const { page } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
  });

  return {
    page,
    extraPageProps: {
      hostname,
    },
  };
};

PartnerHomepage.defaultProps = {
  page: {
    body: [],
  },
  siteName: '',
};

PartnerHomepage.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    body: PropTypes.arrayOf(PropTypes.shape({})),
    carousel: PropTypes.arrayOf(PropTypes.shape({})),
    heroTitle: PropTypes.string.isRequired,
    heroSubtitle: PropTypes.string,
  }),
  siteName: PropTypes.string,
  hostname: PropTypes.string.isRequired,
};

export default PartnerHomepage;
